import React from 'react';

export const HALF_WIDTH = { upper: 28.5, lower: 30 };

export const NAME = { upper: 'Ikkinchi katta oziq tish', lower: 'Ikkinchi katta oziq tish' };

/*
 * Upper second molar: the crown is more rhomboidal than the first molar and its
 * distal half is reduced, so the distal cusp barely shows. Still three roots,
 * but they are closer together and lean distally instead of diverging.
 */
const UPPER_OUTLINE = `M 29 44
    C 25.2 52 22.6 58.4 22 66
    C 21.6 74 23.6 82 26.6 86.4
    C 28.2 88.6 29.8 90.4 31.8 92
    C 33.8 93.6 36.6 94.2 38.4 92.6
    C 40.2 90.8 42 89.2 43.6 88.4
    C 44.6 87.9 45 87.9 45.8 88.5
    C 47.6 89.8 49.4 91.6 51.4 92.8
    C 53.6 94.2 56 93.6 57.6 92
    C 59 90.6 60.4 89.4 61.4 88.8
    C 62.2 88.4 62.7 88.5 63.4 89.2
    C 65 90.4 66.8 91.6 68.4 92.2
    C 70.2 92.9 71.6 91.8 72.8 89.4
    C 75.4 84.6 77.6 74.4 77.8 66.4
    C 78 58.2 74.8 50 71 44
    C 70.8 40 71 34.4 71.8 28.4
    C 72.4 23 71.8 18 70 16.2
    C 68.2 14.4 65.4 16 64.4 20.2
    C 63.4 24.4 62.6 30.4 62 34.8
    C 61.6 36.8 61 38 60.2 38.6
    C 59.6 37.4 59.2 35.6 58.8 33
    C 58 26.4 57.2 17.4 56.4 13
    C 55.6 9.4 52 8.6 50 11.2
    C 48 13.8 47 22.6 46.2 31
    C 45.8 34.4 45.2 37.2 44.2 38.6
    C 43.4 37.6 42.8 35.8 42.2 33.2
    C 41.2 27 39.8 19.4 38.2 17
    C 36.4 14.4 33.2 15.4 32.2 19.4
    C 31.4 23 31.6 28.6 32 34
    C 32.2 38 30 41 29 44 Z`;

const UPPER_CERVICAL = 'M 29 44 C 37 38.2 63 38.2 71 44';

const UPPER_DETAILS = [
    'M 44.8 88.1 C 44.4 82 44.2 75 44.8 68',
    'M 62.4 88.6 C 62.2 83.6 62.2 78 62.6 73',
    'M 35.6 93.6 C 36 86.8 36.2 80.2 35.8 74.2',
    'M 54 93.6 C 54.4 87.2 54.6 81.2 54.2 76',
    'M 44.2 38.6 C 43.6 40.8 43.2 42.6 43 44.2',
    'M 60.2 38.6 C 60.6 40.8 61 42.6 61.2 44.2',
];

/*
 * Lower second molar: a regular four-cusped crown, so a single buccal groove
 * divides the two visible cusps. Two roots, closer together and both inclined
 * distally.
 */
const LOWER_OUTLINE = `M 30 50
    C 26.6 40 23.2 30.4 22.4 22.4
    C 22 15.6 24.2 9.6 27.8 6.8
    Q 30.2 4.8 33.6 5.6
    L 37.6 4.4
    Q 39.8 3.6 41.2 5.6
    L 47.6 11.6
    Q 49 12.8 50.4 11.4
    L 57 5.6
    Q 59.2 3.6 61.4 5.6
    L 65.4 4.6
    Q 68.8 3.8 71.2 5.8
    C 74.8 8.6 77 14.6 76.6 21.6
    C 76.2 29.6 73.2 40 70 50
    C 71.6 58 72 68 71 77
    C 70.2 84.6 68.4 90.6 65.6 93
    Q 62.2 96 58.6 94.6
    C 55.8 93.4 53.8 88.8 52.6 82.4
    C 51.4 76 50.8 66.4 50.6 59.6
    C 50.5 58.2 50.3 57.6 50 57.2
    C 49.7 57.6 49.5 58.2 49.4 59.6
    C 49 66.4 48.4 76 47.2 82.4
    C 46 88.8 44 93.4 41.2 94.6
    Q 37.6 96 34.2 93
    C 31.4 90.6 29.6 84.6 28.8 77
    C 27.8 68 28.2 58 30 50 Z`;

const LOWER_CERVICAL = 'M 30 50 C 38 55.4 62 55.4 70 50';

const LOWER_DETAILS = [
    /* Single buccal groove between the two visible cusps. */
    'M 49 12 C 48.6 19.4 48.4 27 49 34',
    'M 37.4 5.6 C 37.8 12.6 38 19.4 37.6 25.4',
    'M 59.4 6 C 59.8 13 60 19.6 59.6 25.6',
    'M 50 57.2 C 50 54.6 50 52.4 50 50.4',
    'M 40.4 60 C 40.6 70 40.4 80 39.6 88',
];

export default function SecondMolar({ jaw }) {
    const isUpper = jaw === 'upper';

    return (
        <>
            <path d={isUpper ? UPPER_OUTLINE : LOWER_OUTLINE} className="dental-tooth__body" />
            <path d={isUpper ? UPPER_CERVICAL : LOWER_CERVICAL} className="dental-tooth__line" />
            {(isUpper ? UPPER_DETAILS : LOWER_DETAILS).map((d) => (
                <path key={d} d={d} className="dental-tooth__detail" />
            ))}
        </>
    );
}
